import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Box, FormControl, IconButton, MenuItem, Select, Stack, Typography } from "@mui/material";
import { validateStringField } from "../../../utils/Validation";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { IMaintenanceFile, MaintenanceStatus } from "../../../services/registry/MaintenanceService";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

export interface MaintenanceMessageFormProps {
  visible: boolean;
  initialData?: IMaintenanceFile;
  onAccept: (data: IMaintenanceFile) => Promise<void>;
  onDismiss: () => void;
}

interface LocalizedMessageEntry {
  locale: string;
  message: string;
}

const MaintenanceMessageForm = ({ visible, initialData, onAccept, onDismiss }: MaintenanceMessageFormProps) => {
  const [loading, setLoading] = useState(false);
  const [defaultMessageError, setDefaultMessageError] = useState<string | undefined>(undefined);
  const [localizedMessages, setLocalizedMessages] = useState<LocalizedMessageEntry[]>([]);
  const [status, setStatus] = useState<MaintenanceStatus>(MaintenanceStatus.SCHEDULED);
  const [startTime, setStartTime] = useState<DateTime | null>(null);
  const [endTime, setEndTime] = useState<DateTime | null>(null);

  const refDefaultMessage = useRef<TextFieldProps>(null);

  useEffect(() => {
    if (visible && initialData) {
      // Convert localizedMessages object to array
      const entries = Object.entries(initialData.localizedMessages || {}).map(([locale, message]) => ({
        locale,
        message,
      }));
      setLocalizedMessages(entries);
      setStatus(initialData.status || MaintenanceStatus.SCHEDULED);
      setStartTime(initialData.maintenanceStartedAt ? DateTime.fromSeconds(initialData.maintenanceStartedAt) : null);
      setEndTime(initialData.maintenanceEndsAt ? DateTime.fromSeconds(initialData.maintenanceEndsAt) : null);
    } else if (!visible) {
      // Reset when dialog closes
      setLocalizedMessages([]);
      setDefaultMessageError(undefined);
      setStatus(MaintenanceStatus.SCHEDULED);
      setStartTime(null);
      setEndTime(null);
    }
  }, [visible, initialData]);

  const dialogCloseHandler = (event: unknown, reason: string) => {
    if (reason !== "backdropClick") {
      onDismiss();
    }
  };

  const okButtonClickHandler = async () => {
    if (!refDefaultMessage.current) {
      return;
    }

    const defaultMessage = validateStringField(refDefaultMessage.current.value as string, setDefaultMessageError, 1, 500);

    if (!defaultMessage) {
      return;
    }

    // Convert array back to object
    const localizedMessagesObj: { [locale: string]: string } = {};
    localizedMessages.forEach((entry) => {
      if (entry.locale && entry.message) {
        localizedMessagesObj[entry.locale] = entry.message;
      }
    });

    const data: IMaintenanceFile = {
      status,
      defaultMessage,
      localizedMessages: localizedMessagesObj,
      maintenanceStartedAt: startTime?.toUnixInteger(),
      maintenanceEndsAt: endTime?.toUnixInteger(),
    };

    setLoading(true);
    try {
      await onAccept(data);
    } finally {
      setLoading(false);
    }
  };

  const addLocalizedMessage = () => {
    setLocalizedMessages([...localizedMessages, { locale: "", message: "" }]);
  };

  const removeLocalizedMessage = (index: number) => {
    const newMessages = [...localizedMessages];
    newMessages.splice(index, 1);
    setLocalizedMessages(newMessages);
  };

  const updateLocalizedMessage = (index: number, field: "locale" | "message", value: string) => {
    const newMessages = [...localizedMessages];
    newMessages[index][field] = value;
    setLocalizedMessages(newMessages);
  };

  return (
    <Dialog open={visible} onClose={dialogCloseHandler} maxWidth="md" fullWidth>
      <DialogTitle>Edit Maintenance Message</DialogTitle>
      <DialogContent>
        <DialogContentText>Update the maintenance message details.</DialogContentText>

        <FormControl sx={{ m: 1, minWidth: 540 }} fullWidth>
          <Select
            labelId="status-label"
            id="status-selector"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as MaintenanceStatus)}
          >
            <MenuItem value={MaintenanceStatus.SCHEDULED}>SCHEDULED</MenuItem>
            <MenuItem value={MaintenanceStatus.IN_PROGRESS}>IN_PROGRESS</MenuItem>
            <MenuItem value={MaintenanceStatus.COMPLETED}>COMPLETED</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ m: 1, minWidth: 540 }} fullWidth>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateTimePicker label="Maintenance Start Time" value={startTime} onChange={(value) => setStartTime(value)} />
          </LocalizationProvider>
        </FormControl>

        <FormControl sx={{ m: 1, minWidth: 540 }} fullWidth>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateTimePicker label="Maintenance End Time" value={endTime} onChange={(value) => setEndTime(value)} />
          </LocalizationProvider>
        </FormControl>

        <FormControl sx={{ m: 1, minWidth: 540 }} fullWidth>
          <TextField
            margin="dense"
            fullWidth
            variant="standard"
            inputRef={refDefaultMessage}
            id="defaultMessage"
            label="Default Message"
            type="text"
            multiline
            rows={3}
            defaultValue={initialData?.defaultMessage || ""}
            error={defaultMessageError !== undefined}
            helperText={defaultMessageError}
          />
        </FormControl>

        <Box sx={{ m: 1, mt: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Localized Messages</Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addLocalizedMessage}>
              Add Localized Message
            </Button>
          </Stack>

          {localizedMessages.map((entry, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                label="Locale (e.g., en-US)"
                variant="outlined"
                size="small"
                value={entry.locale}
                onChange={(e) => updateLocalizedMessage(index, "locale", e.target.value)}
                sx={{ width: "200px" }}
              />
              <TextField
                label="Message"
                variant="outlined"
                size="small"
                multiline
                rows={2}
                value={entry.message}
                onChange={(e) => updateLocalizedMessage(index, "message", e.target.value)}
                sx={{ flexGrow: 1 }}
              />
              <IconButton color="error" onClick={() => removeLocalizedMessage(index)}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}

          {localizedMessages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No localized messages added. Click &quot;Add Localized Message&quot; to add one.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss} color="error" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={okButtonClickHandler} disabled={loading}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceMessageForm;
